import React from 'react';
import { connect } from 'react-redux';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import * as Yup from 'yup';
import { useFormik } from 'formik';
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  CssBaseline,
  FormHelperText,
  Grid,
  Link,
  Paper,
  TextField,
  Typography,
} from '@mui/material';
//import makeStyles from '@mui/styles/makeStyles';
import { styled, useTheme } from '@mui/material/styles';
import ReactGA from 'react-ga';
import { COUNTRIES, USStates, CAProvinces } from 'utils/constant.js';
import { demoRequest } from 'actions/demo-request-action';
import ToastHandler from 'components/Toast-Handler/toast-handler';
import SelectField from 'components/Select-Field/select-field';
import Terms from 'containers/terms';
import TagManager from 'react-gtm-module'
import NotificationDialog from 'components/dialogs/NotificationDialog';
import { tokens } from 'theme/tokens';

TagManager.dataLayer({
  dataLayer: {
    event: 'pageview',
    pagePath: '/demo/request',
    pageTitle: 'demo-request',
  },
});

const DemoRequestPage = ({ toast, isProcessing }) => {
  const [open, setOpen] = React.useState(false);
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  const RootContainer = styled(Grid)(({ }) => ({
    height: '100vh',
  }));

  const CustomImage = styled('img')(({ theme }) => ({
    backgroundRepeat: 'no-repeat',
    backgroundColor:
      theme.palette.mode === 'light' ? colors.neutral[50] : colors.neutral[900],
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    width: '100%',
    height: '100%',
  }));

  const CustomPaper = styled('div')(({ }) => ({
    marginY: 8,
    marginX: 4, 
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  }));

  const Avatar = styled('div')(({ }) => ({
    margin: 1,
    backgroundColor: colors.secondary.main,
  }));

  const handleOpen = () => {
    setOpen(true);
  };
  const handleClose = () => {
    setOpen(false);    
  }; 
  const navigate = useNavigate();  

  const formik = useFormik({
    initialValues: {
      company: '',
      email: '',
      firstName: '',
      lastName: '',
      street: '',
      city: '',
      state: '',
      country:'',
      password: '',
      confirmPassword: '',
      accountPlan: 'REGULAR',
      policy: false
    },

    validationSchema: Yup.object().shape({
      company: Yup.string().max(100).required('Company Name is required.'),
      email: Yup.string().email('Must be a valid email').max(100).required('Email is required'),
      firstName: Yup.string().max(50).required('FirstName is required'),
      lastName: Yup.string().max(50).required('LastName is required'),
      state: Yup.string().min(2).max(25).required('State Name is required.'),
      country: Yup.string().max(25).required('Country Name is required.'),
      policy: Yup.boolean().oneOf([true], 'This field must be checked')
    }),

    onSubmit: values => {
      const trimmed_data = {};
      Object.keys(values).forEach(key => trimmed_data[key] = typeof(values[key]) === 'string' ? values[key].trim() : values[key]);
      demoRequest(trimmed_data);
      ReactGA.event({
        category: 'DemoRequest',
        action: 'Demo Request Submitted.'
      });      
    }
  });

  if (toast) {
    if (toast.type === 'SUCCESS') {
      navigate('/', { replace: true });
    }
  }

  return (
    <RootContainer container component="main">
      <CssBaseline />
      <Grid item xs={12} sm={8} md={5} component={Paper} elevation={6} square>
        <CustomPaper>
          <form onSubmit={formik.handleSubmit}>
            <Box mb={3}>
              <Typography variant='h2'>
                Request a Demo
              </Typography>
              <Typography      
                gutterBottom
                variant="body2"
                color='secondary'
              >
                Ease your dispatch management operations with our software
              </Typography>              
            </Box>
            {isProcessing ? <CircularProgress style={{position: 'absolute', top: '50%', marginLeft: '50%' }}/>: null}
            {toast ? <ToastHandler component="DemoRequestPage"
              toast={toast}
              type={toast.type} /> : ''}
            <TextField
              error={Boolean(formik.touched.company && formik.errors.company)}
              fullWidth
              helperText={formik.touched.company && formik.errors.company}
              label="Company Name"
              margin="normal"
              name="company"
              onBlur={formik.handleBlur}
              onChange={formik.handleChange}
              value={formik.values.company}
              variant="outlined"
            />
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={6}>
                <TextField
                  error={Boolean(formik.touched.firstName && formik.errors.firstName)}
                  fullWidth
                  helperText={formik.touched.firstName && formik.errors.firstName}
                  label="First Name"
                  margin="normal"
                  name="firstName"
                  onBlur={formik.handleBlur}
                  onChange={formik.handleChange}
                  value={formik.values.firstName}
                  variant="outlined"
                />
              </Grid>
              <Grid item xs={12} sm={6} md={6}>
                <TextField
                  error={Boolean(formik.touched.lastName && formik.errors.lastName)}
                  fullWidth
                  helperText={formik.touched.lastName && formik.errors.lastName}
                  label="Last Name"
                  margin="normal"
                  name="lastName"
                  onBlur={formik.handleBlur}
                  onChange={formik.handleChange}
                  value={formik.values.lastName}
                  variant="outlined"
                />
              </Grid>
              <Grid item xs={12} sm={6} md={6}>
                <SelectField
                  label="Country"
                  name="country"
                  value={formik.values.country}
                  variant="outlined"
                  fullWidth         
                  error={Boolean(formik.touched.country && formik.errors.country)}
                  helperText={formik.touched.country && formik.errors.country}
                  onBlur={formik.handleBlur}
                  onChange={formik.handleChange}
                  options={COUNTRIES}
                />
              </Grid>        
              <Grid item xs={12} sm={6} md={6}>
                <SelectField
                  label="State"
                  name="state"
                  select
                  value={formik.values.state}
                  variant="outlined"
                  fullWidth         
                  error={Boolean(formik.touched.state && formik.errors.state)}
                  helperText={formik.touched.state && formik.errors.state}
                  onBlur={formik.handleBlur}
                  onChange={formik.handleChange}
                  options={formik.values['country'] === 'USA' ? USStates : CAProvinces}
                />
              </Grid>
            </Grid>
            <TextField
              error={Boolean(formik.touched.email && formik.errors.email)}
              fullWidth
              helperText={formik.touched.email && formik.errors.email}
              label="Email Address"
              margin="normal"
              name="email"
              onBlur={formik.handleBlur}
              onChange={formik.handleChange}
              type="email"
              value={formik.values.email}
              variant="outlined"
            />

            {/* <Box
              justifyContent='space-between'
              display="flex"
            >
              <Typography style={{color: '#ff9100', fontWeight: 600, marginTop: 10}}>Choose Plan</Typography>
              <FormControl component="fieldset">                
                <RadioGroup row aria-label="plan" name="accountPlan" value={formik.values.accountPlan} onChange={formik.handleChange}>
                  <FormControlLabel labelPlacement="end" value="basic" control={<Radio name="accountPlan" value="BASIC" />}
                    label={<Typography
                      color="textSecondary"
                    >
                      <Link
                        color="primary"
                        component={RouterLink}
                        to="#"
                        underline="always"
                        variant="h6"
                      >
                      Basic
                      </Link>
                    </Typography>}
                  />                  
                  <FormControlLabel labelPlacement="end" value="regular" control={<Radio name="accountPlan" value="REGULAR" />}
                    label={<Typography
                      color="textSecondary"
                    >
                      <Link
                        color="primary"
                        component={RouterLink}
                        to="#"
                        underline="always"
                        variant="h6"
                      >
                        Regular
                      </Link>
                    </Typography>}
                  />
                  <FormControlLabel labelPlacement="end" value="premium" control={<Radio name="accountPlan" value="PREMIUM" />}
                    label={<Typography
                      color="textSecondary"
                    >
                      <Link
                        color="primary"
                        component={RouterLink}
                        to="#"
                        underline="always"
                        variant="h6"
                      >
                        Premium
                      </Link>
                    </Typography>} />
                </RadioGroup>
              </FormControl>
            </Box> */}
            {/* {Boolean(formik.touched.accountPlan && formik.errors.accountPlan) && (
              <FormHelperText error>
                {formik.errors.accountPlan}
              </FormHelperText>
            )} */}
            <Box
              alignItems="center"
              display="flex"
              ml={-1}
            >
              <Checkbox
                checked={formik.values.policy}
                name="policy"
                onChange={formik.handleChange}
              />
              <Typography
                color="textSecondary"
                variant="body1"
              >
                I have read the
                {' '}
                <Link
                  color="primary"
                  onClick={handleOpen}
                  underline="always"
                  variant="h6"
                >
                  Terms and Conditions
                </Link>
              </Typography>
              <NotificationDialog
                open={open}
                title={'Terms & Conditions'}
                subTitle={'Please read following terms and conditions.'}                
                children={Terms}
                maxWidth={'md'}
                handlePrimary={handleClose}
                handleClose={handleClose}
              />
              {/* <ConfirmationDialog
                isOpen={open}
                title={'Delete Broker'}
                children={TermsAndPolicy}
                handlePrimary={handleClose}
                handleSecondary={handleClose}
                handleClose={handleClose}
              /> */}
            </Box>
            {Boolean(formik.touched.policy && formik.errors.policy) && (
              <FormHelperText error>
                {formik.errors.policy}
              </FormHelperText>
            )}
            <Box my={2}>
              <Button
                color="primary"
                fullWidth
                size="large"
                type="submit"
                variant="contained"
              >
                Request a Demo
              </Button>
            </Box>
            <Typography
              color="textSecondary"
              variant="body1"
            >
              Have an account?
              {' '}
              <Link component={RouterLink} to="/login" variant="h6" underline="hover">
                Sign in
              </Link>
            </Typography>
          </form>
        </CustomPaper>
      </Grid>
    </RootContainer>
  );
};

const mapStateToProps = state => {
  const {account, isProcessing, toast} = state.demoRequestReducer;
  return {
    account,
    isProcessing,
    toast
  };
};

const mapDispatchToProps = dispatch => {
  return {
    newDemoRequest: user => dispatch(demoRequest(user)),
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(DemoRequestPage);